"use client"

import { Bot, Wrench } from "lucide-react"
import type { MprcData } from "@/hooks/use-mprc-socket"
import { SYSTEM_NODES } from "@/lib/mprc-config"

interface RobotStatusBarProps {
  data: MprcData
}

const STATUS_CLASSES: Record<string, string> = {
  idle:   "bg-blue-500/15 text-blue-400 border-blue-500/25",
  moving: "bg-amber-500/15 text-amber-400 border-amber-500/25",
  error:  "bg-red-500/15 text-red-400 border-red-500/25",
  ready:  "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
}

function statusClass(val: string | null | undefined) {
  if (!val) return "bg-muted/40 text-muted-foreground border-border/30"
  return STATUS_CLASSES[String(val).toLowerCase()] ?? "bg-muted/40 text-muted-foreground border-border/30"
}

function ToolChangerStatus({ data }: { data: MprcData }) {
  const engaged = Boolean(data[SYSTEM_NODES.toolChangerEngage])
  const disengaged = Boolean(data[SYSTEM_NODES.toolChangerDisengage])

  let label = "Unknown"
  let cls = "bg-muted/40 text-muted-foreground border-border/30"

  if (engaged && !disengaged) {
    label = "Engaged"
    cls = "bg-emerald-500/15 text-emerald-400 border-emerald-500/25"
  } else if (!engaged && disengaged) {
    label = "Disengaged"
    cls = "bg-amber-500/15 text-amber-400 border-amber-500/25"
  } else if (!engaged && !disengaged) {
    label = "Idle"
    cls = "bg-blue-500/15 text-blue-400 border-blue-500/25"
  }

  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono font-semibold border ${cls}`}>
      {label}
    </span>
  )
}

export function RobotStatusBar({ data }: RobotStatusBarProps) {
  const robotStatus = data[SYSTEM_NODES.robotStatus]
  const statusStr = robotStatus != null ? String(robotStatus) : null

  return (
    <>
      <div className="flex items-center gap-1.5">
        <Bot className="size-3.5 text-muted-foreground/70 shrink-0" />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Robot
        </span>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono font-semibold border ${statusClass(statusStr)}`}
        >
          {statusStr ?? "—"}
        </span>
      </div>

      <div className="w-px h-4 bg-border/50" />

      <div className="flex items-center gap-1.5">
        <Wrench className="size-3.5 text-muted-foreground/70 shrink-0" />
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Tool Changer
        </span>
        <ToolChangerStatus data={data} />
      </div>
    </>
  )
}
